/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from 'react'
import { QueryClient } from 'react-query'
import { dehydrate } from 'react-query/hydration'
import { GetServerSidePropsContext } from 'next'
import { decodeQueryParams } from 'use-query-params'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'

import {
  MainLayoutContentNew,
  MainLayoutNew,
  MainLayoutSidebarNew
} from 'src/features/layouts/main-layout-new'
import {
  removeEmptyFieldObject,
  SERVICES_URL,
  SearchBar,
  useTranslation,
  goodsSeo,
  goodsSeoEn
} from '@plandi/common'
import { BreadCrumbs } from 'src/features/breadcrumbs/breadcrumbs'
import { Modal, Typography } from '@material-ui/core'
import { Close } from '@material-ui/icons'
import {
  SeoTextBlock,
  SEO_TEXTS,
  sharedApi,
  useQuerySeoTexts,
  TRANSLATE,
  GOODS
} from 'src/shared'
import { TabsJob } from 'src/features/tabs-job'
import { CreateButtons } from 'src/features/createButtons'
import { components } from '@plandi/common/src/generated'
import {
  GoodsFiltersSidebarClear,
  GoodsFiltersSidebarNew,
  GoodsFiltersSortNew,
  GoodsMobileFilters,
  GoodsModalFiltersRubrics,
  HelpFindProduct,
  ProductFiltersQueryParamConfigMap,
  ProductList,
  ProductTags,
  goodsApi,
  productKeys,
  useGoodsFilters,
  useGoodsStyles,
  useQueryProductTags
} from 'src/features/goods'
import { TagTextWrapper } from 'src/features/tag-text-wrapper'

export const getServerSideProps = async ({
  query,
  locale
}: GetServerSidePropsContext) => {
  const queryClient = new QueryClient()

  const decodeParams = decodeQueryParams(
    ProductFiltersQueryParamConfigMap,
    query
  )

  const params = removeEmptyFieldObject({
    ...decodeParams,
    categoryIds: decodeParams.categoryIds?.join(','),
    rubricIdIds: decodeParams.rubricIds?.join(','),
    rubricIds: decodeParams.rubricIds?.join(',')
  })

  const param = {
    tag: decodeParams.tag
  }

  await Promise.all([
    queryClient.prefetchQuery([productKeys.rubrics, locale], () =>
      goodsApi.getProductRubrics(locale ?? 'en')
    ),
    queryClient.prefetchQuery([productKeys.list(params), locale], () =>
      goodsApi.getGoods(locale ?? 'en', params)
    ),
    queryClient.prefetchQuery([SEO_TEXTS, locale], () =>
      sharedApi.getSEOText(locale ?? 'en')
    ),
    queryClient.prefetchQuery(TRANSLATE, () => sharedApi.getTranslate(locale)),
    queryClient.prefetchQuery([GOODS, locale], () =>
      goodsApi.getProductTags(locale ?? 'en')
    )
  ])

  const tags = await goodsApi.getProductTags(locale ?? 'en')
  const currentTag = param.tag ? tags.find((tag) => tag.id === param.tag) : null

  return {
    props: {
      locale: locale,
      dehydratedState: dehydrate(queryClient),
      currentTag
    }
  }
}

const GoodsPage = (props: {
  locale: string
  currentTag?: components['schemas']['ProductTagResource']
}) => {
  const [goodsCount, setGoodsCount] = useState(0)
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false)
  const theme = useTheme()
  const classes = useGoodsStyles()
  const { data: translate } = useTranslation()
  const { tag: tagId } = useGoodsFilters()
  // TODO: remove on success
  const { title, description } = goodsSeo
  const { titleEn, descriptionEn } = goodsSeoEn
  const { data: seoTexts } = useQuerySeoTexts()
  const { data: tags } = useQueryProductTags()

  const changedTag = useMemo(() => {
    if (!tagId) return null
    if (tagId === props.currentTag?.id) return props.currentTag
    return tags?.find((tag) => tag.id === tagId)
  }, [props.currentTag, tagId, tags])

  const isLaptopPadding = useMediaQuery(theme.breakpoints.between(600, 960))
  const isLaptop = useMediaQuery(theme.breakpoints.up(960))
  const isTablet = useMediaQuery(theme.breakpoints.up(1280))
  const isMobile = useMediaQuery(theme.breakpoints.down(600))

  const shortRow = translate?.shop['projects-page']['count-results']

  const url = SERVICES_URL
  const locale = props.locale

  return (
    <>
      {/* <Head>
        {(config.isStage || config.isProd) && (
          <script src="//code-eu1.jivosite.com/widget/TWVsZUI6qE" async />
        )}
      </Head> */}
      <MainLayoutNew
        searchType="goods"
        className="grayColor"
        withFooter
        title={changedTag?.seoTitle ?? props.locale === 'ru' ? title : titleEn}
        description={
          changedTag?.seoDescription ?? props.locale === 'ru'
            ? description
            : descriptionEn
        }
        currentTag={props.currentTag}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: ' 100%'
          }}
        >
          <BreadCrumbs
            styles={{ marginBottom: isLaptop ? 0 : 20 }}
            breadcrumbInfo={[
              {
                title: translate?.market.title ?? '',
                href: locale === 'ru' ? `${url.shop}/ru` : url.shop
              },
              {
                title: translate?.market?.goods ?? 'Товары',
                href: locale === 'ru' ? `${url.shop}/ru` : url.shop
              }
            ]}
          />
          <TabsJob place="goods" title={changedTag ?? props.currentTag} />
          <ProductTags />
          <div style={{ display: 'flex' }}>
            {isLaptop && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    margin: '6px 0 34px'
                  }}
                >
                  <Typography
                    variant="body2"
                    style={{ color: '#757575', padding: '0 34px 0 0' }}
                  >
                    {`${shortRow}: ${goodsCount}`}
                  </Typography>
                  <GoodsFiltersSidebarClear />
                </div>
                <SearchBar
                  className={classes.searchBar}
                  typeForSearch={'product'}
                />
                <MainLayoutSidebarNew hideMobile>
                  <GoodsFiltersSidebarNew translate={translate} />
                </MainLayoutSidebarNew>
              </div>
            )}
            <MainLayoutContentNew>
              {isLaptop && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 27,
                    marginTop: isTablet ? 0 : 5
                  }}
                >
                  <CreateButtons place="product" />
                  <div
                    style={{
                      display: 'flex',
                      gap: 20,
                      alignItems: 'center'
                    }}
                  >
                    <HelpFindProduct translate={translate} />
                    {isTablet && (
                      <GoodsFiltersSortNew
                        class={'sort'}
                        translate={translate}
                      />
                    )}
                  </div>
                </div>
              )}
              {!isLaptop && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginBottom: 0
                  }}
                >
                  <GoodsMobileFilters
                    setIsSidebarOpen={(isOpen: boolean) => {
                      setIsSidebarOpen(isOpen)
                    }}
                    translate={translate}
                    typeForSearch={'product'}
                  />
                  <div style={{ padding: isLaptopPadding ? '0 24px' : 0 }}>
                    <CreateButtons place="product" />
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 20
                      }}
                    >
                      <Typography
                        variant="body2"
                        style={{ color: '#757575', padding: 0 }}
                      >
                        {`${shortRow}: ${goodsCount}`}
                      </Typography>
                      <HelpFindProduct translate={translate} />
                    </div>
                  </div>
                </div>
              )}

              <ProductList
                setGoodsCount={setGoodsCount}
                translate={translate}
                locale={props.locale}
              />
            </MainLayoutContentNew>
          </div>
          {tagId &&
          (changedTag?.description || props?.currentTag?.description) ? (
            <TagTextWrapper
              text={changedTag?.description ?? props?.currentTag?.description}
            />
          ) : (
            <SeoTextBlock text={seoTexts?.goods} />
          )}
        </div>
      </MainLayoutNew>
      <Modal
        open={isSidebarOpen}
        className={classes.sidebarModal}
        style={{
          top: isMobile ? 0 : 65,
          border: 'none',
          zIndex: isMobile ? 1300 : 1000
        }}
      >
        <div
          style={{
            background: 'white',
            paddingBottom: '13.5px'
          }}
        >
          <div className={classes.modalHeader}>
            <Typography className={classes.filterTitle}>
              {translate?.filters ?? 'Фильтры'}
            </Typography>
            <Close
              onClick={() => setIsSidebarOpen(false)}
              style={{ cursor: 'pointer' }}
            />
          </div>
          <GoodsModalFiltersRubrics translate={translate} />
        </div>
      </Modal>
    </>
  )
}

export default GoodsPage
